import type { SupabaseClient } from '@supabase/supabase-js';
import {
  resolveComplianceSurfaceVisibility,
  type ComplianceSurfaceFacts,
} from '@statura/utils/compliance-obligations/visibility';

/** Roles accepted by get_compliance_surface_visibility_controlled (migration
 * 20280300000001_chsp_applicability_reader_parity.sql). Keep in sync with its
 * regulated_actor_context allowlist. */
export const COMPLIANCE_SURFACE_VISIBILITY_RPC_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
] as const;

export type ComplianceVisibilityMode = 'user' | 'system';

/** Fixed metadata only: never retain database messages, details or hints. */
export class ComplianceVisibilityReadError extends Error {
  readonly code: string;
  constructor(code: unknown) {
    super('Provider compliance facts are unavailable. Review is required.');
    this.name = 'ComplianceVisibilityReadError';
    this.code =
      typeof code === 'string' &&
      /^(?:[0-9A-Z]{5}|PGRST[0-9]{3}|NOT_AUTHORISED|INVALID_RESPONSE)$/.test(code)
        ? code
        : 'READER_FAILED';
  }
}

/** Explicit caller mode; absence of a JWT never selects system authority. */
export async function loadComplianceSurfaceVisibility(
  sb: SupabaseClient,
  tenantId: string,
  mode: ComplianceVisibilityMode,
) {
  let response;
  try {
    response = await sb.rpc(
      mode === 'system'
        ? 'get_compliance_surface_visibility_system'
        : 'get_compliance_surface_visibility_controlled',
      { p_tenant_id: tenantId },
    );
  } catch (error) {
    throw new ComplianceVisibilityReadError((error as { code?: unknown } | null)?.code);
  }
  const { data, error } = response;
  if (!error && data?.code === 'NOT_AUTHORISED' && mode === 'user') return null;
  if (error || data?.ok !== true || !Array.isArray(data.context?.facets)) {
    throw new ComplianceVisibilityReadError(error?.code ?? data?.code ?? 'INVALID_RESPONSE');
  }
  return resolveComplianceSurfaceVisibility(data.context as ComplianceSurfaceFacts);
}
